<template>
  <Page>
    <div class="max-w-6xl" data-cy="webhooks-hub">
      <h1 class="page-header-1 mb-2">Webhooks</h1>
      <p class="mb-4 max-w-3xl text-sm text-gray-600">
        Webhooks send events from Sera, such as a payment succeeding, to your
        own systems as they happen.
      </p>

      <div v-if="loading" class="flex justify-center py-10">
        <Spinner />
      </div>

      <Alert
        v-else-if="notAvailable"
        type="info"
        data-cy="webhooks-not-available"
        text="Webhooks aren't available on this account yet."
      />

      <Alert v-else-if="loadFailed" type="error" data-cy="webhooks-load-error">
        Webhooks couldn't be loaded.
        <button type="button" class="link ml-1" @click="load">Try again</button>
      </Alert>

      <template v-else>
        <Alert
          v-if="config && !config.enabled"
          type="warning"
          class="mb-4"
          data-cy="webhooks-integration-off"
        >
          Webhooks are turned off, so nothing is sent. Turn them on from the
          Webhooks card in the
          <a href="/marketplace" class="link">Marketplace</a>.
        </Alert>

        <nav
          class="mb-4 border-b border-gray-200"
          aria-label="Webhooks sections"
        >
          <ul class="-mb-px flex flex-wrap text-sm font-medium text-gray-500">
            <li v-for="tab in TABS" :key="tab.key" class="me-2">
              <a
                :href="tabHref(tab.key)"
                class="inline-block rounded-t-lg border-b-2 p-4"
                :class="
                  activeTab === tab.key
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent hover:border-gray-300 hover:text-gray-600'
                "
                :aria-current="activeTab === tab.key ? 'page' : undefined"
                :data-cy="`webhooks-tab:${tab.key}`"
                @click.prevent="selectTab(tab.key)"
                >{{ tab.label }}</a
              >
            </li>
          </ul>
        </nav>

        <WebhookEndpoints
          v-if="activeTab === 'endpoints'"
          :event-types="eventTypes"
          :read-only="readOnly"
        />
        <WebhookDeliveries
          v-else-if="activeTab === 'deliveries'"
          :event-types="eventTypes"
          :read-only="readOnly"
          :initial-endpoint-id="endpointFilter"
        />
        <WebhookEventCatalog
          v-else-if="activeTab === 'events'"
          :event-types="eventTypes"
          :focus-section="sectionParam"
        />
      </template>
    </div>
  </Page>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useStore } from "vuex";

import Page from "@/components/Page.vue";
import Alert from "@/components/Seranova/Alert/Alert.vue";
import Spinner from "@/components/Seranova/Spinner/Spinner.vue";
import type {
  WebhookEventType,
  WebhooksConfig
} from "@/vuex/modules/integrationWebhooks";
import WebhookDeliveries from "./WebhookDeliveries.vue";
import WebhookEndpoints from "./WebhookEndpoints.vue";
import WebhookEventCatalog from "./WebhookEventCatalog.vue";
import { apiError } from "./webhooksHub";

// The tab lives in the query string, not the path: the header breadcrumbs
// are rebuilt on every path change, and this page sets its own trail.
const TABS = [
  { key: "endpoints", label: "Endpoints" },
  { key: "deliveries", label: "Deliveries" },
  { key: "events", label: "Event catalog" }
] as const;
type TabKey = (typeof TABS)[number]["key"];

const store = useStore();
const route = useRoute();
const router = useRouter();

const config = ref<WebhooksConfig | null>(null);
const eventTypes = ref<WebhookEventType[]>([]);
const loading = ref(false);
const loadFailed = ref(false);
// Every hub route answers 404 while the feature is off for the account.
const notAvailable = ref(false);

const activeTab = computed<TabKey>(() => {
  const requested = route.query.tab;
  return (TABS.find((tab) => tab.key === requested)?.key ??
    TABS[0].key) as TabKey;
});

// Set by an endpoint drawer's "View deliveries" link.
const endpointFilter = computed(() =>
  typeof route.query.endpoint_id === "string" ? route.query.endpoint_id : null
);

// Set by an endpoint drawer's "Verifying webhooks" link.
const sectionParam = computed(() =>
  typeof route.query.section === "string" ? route.query.section : null
);

const readOnly = computed(() => !!config.value?.read_only);

function tabHref(key: TabKey): string {
  return key === TABS[0].key
    ? "/marketplace/webhooks"
    : `/marketplace/webhooks?tab=${key}`;
}

function selectTab(key: TabKey) {
  if (key === activeTab.value) return;
  router.replace({
    query: key === TABS[0].key ? {} : { tab: key }
  });
}

async function load() {
  loading.value = true;
  loadFailed.value = false;
  try {
    const [loadedConfig, loadedTypes] = await Promise.all([
      store.dispatch("fetchWebhooksConfig"),
      store.dispatch("fetchWebhookEventTypes")
    ]);
    config.value = loadedConfig;
    eventTypes.value = loadedTypes;
  } catch (error) {
    if (apiError(error, "").status === 404) notAvailable.value = true;
    else loadFailed.value = true;
  } finally {
    loading.value = false;
  }
}

// Drive the header breadcrumbs (Marketplace › Webhooks) as the Zapier page
// does: set after the load, success or failure, so the trail lands after the
// router's deferred regeneration.
onMounted(async () => {
  await load();
  store.dispatch("setBreadcrumbs", [
    { label: "Marketplace", url: "/marketplace" },
    { label: "Webhooks" }
  ]);
});
</script>
